import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import useBasePath from "src/utils/hooks/useBasePath";
import {
  changeStatusCalendar,
  createCalendar,
  deleteCalendar,
  editCalendar,
  fetchCalendar,
  fetchCalendars,
  fetchEmployeeCalendars,
} from "src/Pages/Company/Absences/api";
import {
  ICalendar,
  ICalendarStatusArgs,
  IExcludedDay,
  INewCalendar,
} from "src/Pages/Company/Absences/Calendars/Calendars.models";
import { IFormValues } from "src/Pages/Company/Absences/Calendars/Calendar/CalendarForm";

interface ISaveCalendarMutation {
  companyId: string;
  calendarId?: string;
  isNew: boolean;
}

export const useFetchCalendars = (companyId: string, years: string[] = []) =>
  useQuery<ICalendar[], Error>({
    queryKey: ["calendars", { companyId }],
    queryFn: async ({ signal }) => fetchCalendars({ companyId, years, signal }),
    initialData: [],
  });

export const useFetchCalendar = (calendarId: string) =>
  useQuery<ICalendar, Error>({
    enabled: !!calendarId,
    queryKey: ["calendar", { calendarId }],
    queryFn: async ({ signal }) => fetchCalendar({ calendarId, signal }),
  });

export const useSaveCalendar = ({
  companyId,
  calendarId,
  isNew,
}: ISaveCalendarMutation) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const basePath = useBasePath();

  return useMutation({
    mutationFn: async ({
      formData,
      excludedDays = [],
    }: {
      formData: IFormValues;
      excludedDays: IExcludedDay[];
    }) => {
      const parsedFormData: INewCalendar | ICalendar = {
        ...formData,
        ownerId: companyId,
        excludedDays,
      };

      if (!isNew) {
        parsedFormData.id = calendarId;
      }

      const result = isNew
        ? await createCalendar(parsedFormData)
        : await editCalendar(calendarId!, parsedFormData as ICalendar);
      return result;
    },
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano kalendarz."));
      if (isNew) {
        // A bit of a hack to ensure all state updates are done before redirecting
        // Used for confirmation modal
        setTimeout(() => {
          history.push(`${basePath}/absences/calendars`);
        }, 500);
      } else {
        queryClient.invalidateQueries({
          queryKey: ["calendar", { calendarId }],
        });
      }
    },
    onError: () => {
      dynamicNotification(
        __("Nie udało się zapisać danych."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useDeleteCalendar = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (calendarId: string) => {
      const result = await deleteCalendar(calendarId);

      return result;
    },
    onSuccess: () => {
      dynamicNotification(__("Kalendarz został usunięty."), "success");
      queryClient.invalidateQueries({
        queryKey: ["calendars", { companyId }],
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Nie udało się usunąć kalendarza."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useChangeStatusCalendar = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation<unknown, AxiosError, ICalendarStatusArgs>({
    mutationFn: async ({ calendarId, active }: ICalendarStatusArgs) => {
      const result = await changeStatusCalendar(calendarId, active);

      return result;
    },
    onSuccess: () => {
      dynamicNotification(__("Kalendarz został dezaktywowany."), "success");
      queryClient.invalidateQueries({
        queryKey: ["calendars", { companyId }],
      });
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd. Kalendarz nie został dezaktywowany."),
        "error",
        "top-right",
        7000
      );
    },
  });
};

export const useFetchEmployeeCalendars = ({
  companyId,
  employeeId,
  years = [],
}: {
  companyId: string;
  employeeId: string;
  years: string[];
}) =>
  useQuery<ICalendar[], Error>({
    queryKey: ["employee-calendars", companyId, employeeId, years],
    queryFn: ({ signal }) =>
      fetchEmployeeCalendars({ companyId, employeeId, years, signal }),
  });
